import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import character from '../../assets/images/intake/step10/char.svg';
import { useEmploymentReadinessContext } from '../../hooks/use-form-context.hooks';
import {
  AgreementLevel,
  dispatchUpdateEmploymentReadiness,
} from '../../redux/slice/form-employment-readiness';
import { useAppDispatch } from '../../redux/store';
import logger from '../../services/logger';

interface FormData {
  interviewPracticeIntroduction: string;
  interviewOnTime: string;
  interviewClothingAppropriate: string;
  interviewProvideDocuments: string;
  interviewExpressClearly: string;
  interviewProvideCompleteInformation: string;
  interviewRelevantQualification: string;
  interviewAskRelevantQuestions: string;
  interviewPositiveAttitude: string;
  interviewBodyLanguage: string;
  interviewFeelConfident: string;
}

interface ComponentProps {
  step: number;
  onBack: () => void;
}

export const JobSkillOptions = [
  {
    label: 'Strongly Disagree',
    value: AgreementLevel.STRONGLY_DISAGREE,
  },
  {
    label: 'Disagree',
    value: AgreementLevel.DISAGREE,
  },
  {
    label: 'Neutral',
    value: AgreementLevel.NEUTRAL,
  },
  {
    label: 'Agree',
    value: AgreementLevel.AGREE,
  },
  {
    label: 'Strongly Agree',
    value: AgreementLevel.STRONGLY_AGREE,
  },
];
export const JobInterview: React.FC<ComponentProps> = ({ step, onBack }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    interviewPracticeIntroduction,
    interviewOnTime,
    interviewClothingAppropriate,
    interviewProvideDocuments,
    interviewExpressClearly,
    interviewProvideCompleteInformation,
    interviewRelevantQualification,
    interviewAskRelevantQuestions,
    interviewPositiveAttitude,
    interviewBodyLanguage,
    interviewFeelConfident,
  } = useEmploymentReadinessContext();
  const [selectedPracticeIntroduction, setSelectedPracticeIntroduction] = useState(
    interviewPracticeIntroduction || '',
  );
  const [selectedInterviewOnTime, setSelectedInterviewOnTime] = useState(interviewOnTime || '');
  const [selectedClothingAppropriate, setSelectedClothingAppropriate] = useState(
    interviewClothingAppropriate || '',
  );
  const [selectedProvideDocuments, setSelectedProvideDocuments] = useState(
    interviewProvideDocuments || '',
  );

  const [selectedExpressClearly, setSelectedExpressClearly] = useState(
    interviewExpressClearly || '',
  );
  const [selectedProvideCompleteInformation, setSelectedProvideCompleteInformation] = useState(
    interviewProvideCompleteInformation || '',
  );
  const [selectedRelevantQualification, setSelectedRelevantQualification] = useState(
    interviewRelevantQualification || '',
  );

  const [selectedAskRelevantQuestions, setSelectedAskRelevantQuestions] = useState(
    interviewAskRelevantQuestions || '',
  );
  const [selectedPositiveAttitude, setSelectedPositiveAttitude] = useState(
    interviewPositiveAttitude || '',
  );
  const [selectedBodyLanguage, setSelectedBodyLanguage] = useState(interviewBodyLanguage || '');

  const [selectedFeelConfident, setSelectedFeelConfident] = useState(interviewFeelConfident || '');

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>, field: string) => {
    switch (field as keyof FormData) {
      case 'interviewPracticeIntroduction':
        setSelectedPracticeIntroduction(event.target.value);
        setError({ ...error, interviewPracticeIntroduction: '' });
        break;
      case 'interviewOnTime':
        setSelectedInterviewOnTime(event.target.value);
        setError({ ...error, interviewOnTime: '' });
        break;
      case 'interviewProvideDocuments':
        setSelectedProvideDocuments(event.target.value);
        setError({ ...error, interviewProvideDocuments: '' });
        break;
      case 'interviewExpressClearly':
        setSelectedExpressClearly(event.target.value);
        setError({ ...error, interviewExpressClearly: '' });
        break;
      case 'interviewProvideCompleteInformation':
        setSelectedProvideCompleteInformation(event.target.value);
        setError({ ...error, interviewProvideCompleteInformation: '' });
        break;
      case 'interviewRelevantQualification':
        setSelectedRelevantQualification(event.target.value);
        setError({ ...error, interviewRelevantQualification: '' });
        break;
      case 'interviewAskRelevantQuestions':
        setSelectedAskRelevantQuestions(event.target.value);
        setError({ ...error, interviewAskRelevantQuestions: '' });
        break;
      case 'interviewPositiveAttitude':
        setSelectedPositiveAttitude(event.target.value);
        setError({ ...error, interviewPositiveAttitude: '' });
        break;
      case 'interviewBodyLanguage':
        setSelectedBodyLanguage(event.target.value);
        setError({ ...error, interviewBodyLanguage: '' });
        break;
      case 'interviewFeelConfident':
        setSelectedFeelConfident(event.target.value);
        setError({ ...error, interviewFeelConfident: '' });
        break;
      case 'interviewClothingAppropriate':
        setSelectedClothingAppropriate(event.target.value);
        setError({ ...error, interviewClothingAppropriate: '' });
        break;
    }
  };

  const [error, setError] = useState({
    interviewPracticeIntroduction: '',
    interviewOnTime: '',
    interviewClothingAppropriate: '',
    interviewProvideDocuments: '',
    interviewSatisfactionFromWork: '',
    interviewExpressClearly: '',
    interviewProvideCompleteInformation: '',
    interviewRelevantQualification: '',
    interviewAskRelevantQuestions: '',
    interviewPositiveAttitude: '',
    interviewBodyLanguage: '',
    interviewFeelConfident: '',
  });
  const { handleSubmit } = useForm<FormData>();
  const backButtonClick = () => {
    onBack();
  };
  const submitAssessment: SubmitHandler<FormData> = async () => {
    logger.debug('Submitted job interview skills form.', {
      interviewPracticeIntroduction,
      interviewOnTime,
      interviewClothingAppropriate,
      interviewProvideDocuments,
      interviewExpressClearly,
      interviewProvideCompleteInformation,
      interviewRelevantQualification,
      interviewAskRelevantQuestions,
      interviewPositiveAttitude,
      interviewBodyLanguage,
      interviewFeelConfident,
    });

    const error_message: string = 'Please select an option';
    if (!selectedPracticeIntroduction) {
      setError({ ...error, interviewPracticeIntroduction: error_message });
    } else if (!selectedProvideDocuments) {
      setError({ ...error, interviewProvideDocuments: error_message });
    } else if (!selectedInterviewOnTime) {
      setError({ ...error, interviewOnTime: error_message });
    } else if (!selectedExpressClearly) {
      setError({ ...error, interviewExpressClearly: error_message });
    } else if (!selectedProvideCompleteInformation) {
      setError({
        ...error,
        interviewProvideCompleteInformation: error_message,
      });
    } else if (!selectedRelevantQualification) {
      setError({ ...error, interviewRelevantQualification: error_message });
    } else if (!selectedAskRelevantQuestions) {
      setError({ ...error, interviewAskRelevantQuestions: error_message });
    } else if (!selectedPositiveAttitude) {
      setError({ ...error, interviewPositiveAttitude: error_message });
    } else {
      const dto: Record<keyof FormData, AgreementLevel> = {
        interviewPracticeIntroduction: selectedPracticeIntroduction as AgreementLevel,
        interviewOnTime: selectedInterviewOnTime as AgreementLevel,
        interviewClothingAppropriate: selectedClothingAppropriate as AgreementLevel,
        interviewProvideDocuments: selectedProvideDocuments as AgreementLevel,
        interviewExpressClearly: selectedExpressClearly as AgreementLevel,
        interviewProvideCompleteInformation: selectedProvideCompleteInformation as AgreementLevel,
        interviewRelevantQualification: selectedRelevantQualification as AgreementLevel,
        interviewAskRelevantQuestions: selectedAskRelevantQuestions as AgreementLevel,
        interviewPositiveAttitude: selectedPositiveAttitude as AgreementLevel,
        interviewBodyLanguage: selectedBodyLanguage as AgreementLevel,
        interviewFeelConfident: selectedFeelConfident as AgreementLevel,
      };
      await dispatch(dispatchUpdateEmploymentReadiness(dto));
      navigate(`/pre-employment/employment-readiness/step${step + 1}`);
    }
  };

  useEffect(() => {
    if (interviewPracticeIntroduction) {
      setSelectedPracticeIntroduction(interviewPracticeIntroduction);
    }
    if (interviewOnTime) {
      setSelectedInterviewOnTime(interviewOnTime);
    }
    if (interviewProvideDocuments) {
      setSelectedProvideDocuments(interviewProvideDocuments);
    }
    if (interviewExpressClearly) {
      setSelectedExpressClearly(interviewExpressClearly);
    }
    if (interviewProvideCompleteInformation) {
      setSelectedProvideCompleteInformation(interviewProvideCompleteInformation);
    }
    if (interviewRelevantQualification) {
      setSelectedRelevantQualification(interviewRelevantQualification);
    }
    if (interviewAskRelevantQuestions) {
      setSelectedAskRelevantQuestions(interviewAskRelevantQuestions);
    }
    if (interviewPositiveAttitude) {
      setSelectedPositiveAttitude(interviewPositiveAttitude);
    }
    if (interviewBodyLanguage) {
      setSelectedBodyLanguage(interviewBodyLanguage);
    }
    if (interviewFeelConfident) {
      setSelectedFeelConfident(interviewFeelConfident);
    }
    if (interviewClothingAppropriate) {
      setSelectedClothingAppropriate(interviewClothingAppropriate);
    }
  }, [
    interviewPracticeIntroduction,
    interviewOnTime,
    interviewProvideDocuments,
    interviewExpressClearly,
    interviewProvideCompleteInformation,
    interviewRelevantQualification,
    interviewAskRelevantQuestions,
    interviewPositiveAttitude,
    interviewBodyLanguage,
    interviewFeelConfident,
    interviewClothingAppropriate,
  ]);

  return (
    <div>
      <p className="text-lg mt-7 md:px-10">
        Next, let's explore your job interview skills, a crucial aspect of securing a job and making
        a positive impression. You can rate your agreement on a scale from strongly disagree to
        strongly agree.
      </p>
      <p className="text-lg mt-7 md:px-10">
        By exploring these aspects, we'll help you develop the skills and confidence needed to excel
        in interviews, making you a standout candidate in the eyes of potential employers.
      </p>
      <p className="text-lg mt-7 md:px-10">
        You have the flexibility to complete this assessment in one sitting or return later to
        finish the remaining sections. Remember, this journey is all about self-discovery, and we're
        excited to be a part of it with you.
      </p>
      <p className="text-lg mt-7 md:px-10">
        Let's embark on this path toward self-improvement and career success together!
      </p>
      <div className="flex items-center mt-9 mb-10 md:px-10">
        <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">15</div>
        <div>
          <h4 className="text-2xl">
            Click here if you have never taken part in or
            <span className="font-semibold"> do not know how to perform in a job interview.</span>
          </h4>
        </div>
      </div>
      <div className="character absolute top-96 right-[-125px] z-[-1]">
        <img className="" src={character} alt="" />
      </div>
      <form
        className=" md:px-16"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit(submitAssessment)}
      >
        <ol style={{ listStyle: 'upper-latin' }} type="A">
          <li className="mb-5 font-semibold">
            I practice the way I introduce myself before attending a job interview.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedPracticeIntroduction === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'interviewPracticeIntroduction')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="interviewPracticeIntroduction"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.interviewPracticeIntroduction && (
              <p className="text-red-500 font-light !text-sm">
                {error.interviewPracticeIntroduction}
              </p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I am on time for the job interview.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedInterviewOnTime === option.value}
                      onChange={(event) => handleRadioChange(event, 'interviewOnTime')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="interviewOnTime"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.interviewOnTime && (
              <p className="text-red-500 font-light !text-sm">{error.interviewOnTime}</p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I am appropriately dressed and groomed for the position and for the interview setting.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedClothingAppropriate === option.value}
                      onChange={(event) => handleRadioChange(event, 'interviewClothingAppropriate')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="interviewClothingAppropriate"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.interviewClothingAppropriate && (
              <p className="text-red-500 font-light !text-sm">
                {error.interviewClothingAppropriate}
              </p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I provide documents as requested.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedProvideDocuments === option.value}
                      onChange={(event) => handleRadioChange(event, 'interviewProvideDocuments')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="interviewProvideDocuments"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.interviewProvideDocuments && (
              <p className="text-red-500 font-light !text-sm">{error.interviewProvideDocuments}</p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I express myself clearly.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedExpressClearly === option.value}
                      onChange={(event) => handleRadioChange(event, 'interviewExpressClearly')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="interviewExpressClearly"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.interviewExpressClearly && (
              <p className="text-red-500 font-light !text-sm">{error.interviewExpressClearly}</p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I provide complete and appropriate answers without rambling or providing extraneous
            information.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedProvideCompleteInformation === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'interviewProvideCompleteInformation')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="interviewProvideCompleteInformation"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.interviewProvideCompleteInformation && (
              <p className="text-red-500 font-light !text-sm">
                {error.interviewProvideCompleteInformation}
              </p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I state relevant qualifications and connect my experiences to them.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedRelevantQualification === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'interviewRelevantQualification')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="interviewRelevantQualification"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.interviewRelevantQualification && (
              <p className="text-red-500 font-light !text-sm">
                {error.interviewRelevantQualification}
              </p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I ask appropriate and relevant questions.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedAskRelevantQuestions === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'interviewAskRelevantQuestions')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="interviewAskRelevantQuestions"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.interviewAskRelevantQuestions && (
              <p className="text-red-500 font-light !text-sm">
                {error.interviewAskRelevantQuestions}
              </p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I have a positive attitude throughout the interview.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedPositiveAttitude === option.value}
                      onChange={(event) => handleRadioChange(event, 'interviewPositiveAttitude')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="interviewPositiveAttitude"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.interviewPositiveAttitude && (
              <p className="text-red-500 font-light !text-sm">{error.interviewPositiveAttitude}</p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I demonstrate appropriate body language.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedBodyLanguage === option.value}
                      onChange={(event) => handleRadioChange(event, 'interviewBodyLanguage')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="interviewBodyLanguage"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.interviewBodyLanguage && (
              <p className="text-red-500 font-light !text-sm">{error.interviewBodyLanguage}</p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I feel confident that I can communicate my skills and experiences with potential
            employers.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedFeelConfident === option.value}
                      onChange={(event) => handleRadioChange(event, 'interviewFeelConfident')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="interviewFeelConfident"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.interviewFeelConfident && (
              <p className="text-red-500 font-light !text-sm">{error.interviewFeelConfident}</p>
            )}
          </li>
        </ol>
        <div className="flex justify-between mt-20">
          <button
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleSubmit(backButtonClick)}
            className="btn-primary"
          >
            Back
          </button>
          <button className="btn-primary">Next</button>
        </div>
      </form>
    </div>
  );
};
